import {List, ListParams} from 'models/list';
import {Appraisal} from 'models/appraisal';
import {appraisalTemplateQuestionTypes} from 'constants';

const buildAnswersPayload = (questions, draftAnswers) => {
  return questions.map((question) => {
    const value = draftAnswers[question.id];
    const answer = {question_id: question.id};
    if (question.questionType === appraisalTemplateQuestionTypes.MCQ) {
      answer.option_id = value || null;
    } else {
      answer.body = value || null;
    }
    return answer;
  });
};

const state = {
  appraisals: new List(),
  appraisal: null
};

const getters = {
  appraisals: (state) => state.appraisals,
  appraisal: (state) => state.appraisal
};

const actions = {
  async getAppraisals({commit}, options) {
    commit('setLoading', true);
    const response = await this.$app.$http.get('/appraisals', {params: new ListParams(options)});
    if (response.status === 200) {
      commit('setAppraisals', response.data);
      commit('setLoading', false);
    } else {
      commit('setLoading', false);
      this.$app.$snotify.error(this.$app.$t('messages.error.internalServerError'));
    }
    return response;
  },
  async getAppraisal({commit}, id) {
    commit('setLoading', true);
    const response = await this.$app.$http.get(`/appraisals/${id}`);
    if (response.status === 200) {
      commit('setAppraisal', response.data);
      commit('setLoading', false);
    } else {
      commit('setAppraisal', null);
      commit('setLoading', false);
      this.$app.$snotify.error(this.$app.$t('messages.error.notFound',
        {entity: this.$app.$tc('models.appraisal.entity', 1)}));
    }
    return response;
  },
  async saveAppraisalAnswers({commit}, {
    id, questions, answers
  }) {
    commit('setLoading', true);
    const response = await this.$app.$http.put(`/appraisals/${id}/answers`, {
      answers: buildAnswersPayload(questions, answers)
    });
    if (response.status === 200) {
      commit('setAppraisal', response.data);
      commit('setLoading', false);
    } else {
      commit('setLoading', false);
      this.$app.$snotify.error(this.$app.$t('messages.error.updated', {entity: this.$app.$tc('models.appraisal.entity', 1)}));
    }
    return response;
  },
  async submitEmployeeAnswers({commit}, {
    id, questions, answers
  }) {
    return submitStage(this, commit, id, questions, answers, 'submit_employee_answers');
  },
  async submitSupervisorAnswers({commit}, {
    id, questions, answers
  }) {
    return submitStage(this, commit, id, questions, answers, 'submit_supervisor_answers');
  },
  async submitMeetingAnswers({commit}, {
    id, questions, answers
  }) {
    return submitStage(this, commit, id, questions, answers, 'submit_meeting_answers');
  },
  // eslint-disable-next-line no-empty-pattern
  async downloadAppraisalPdf({}, {id, filename}) {
    const response = await this.$app.$http.get(`/appraisals/${id}/export`, {responseType: 'arraybuffer'});
    if (response.status === 200) {
      this.$app.downloadFile(response.data, filename);
    } else {
      this.$app.$snotify.error(this.$app.$t('messages.error.internalServerError'));
    }
    return response;
  }
};

/**
 * Saves the current draft answers then submits the given appraisal stage.
 * @param {Object} store The vuex store instance (module `this`).
 * @param {Function} commit The vuex commit function.
 * @param {string} id The appraisal id.
 * @param {Array} questions The appraisal's questions.
 * @param {Object} answers The draft answers keyed by question id.
 * @param {string} action The submit_* endpoint to call.
 * @return {Object} The axios response.
 */
async function submitStage(store, commit, id, questions, answers, action) {
  commit('setLoading', true);
  const saveResponse = await store.$app.$http.put(`/appraisals/${id}/answers`, {
    answers: buildAnswersPayload(questions, answers)
  });
  if (!store.$app.successCode(saveResponse.status)) {
    commit('setLoading', false);
    store.$app.$snotify.error(store.$app.$t('messages.error.updated', {entity: store.$app.$tc('models.appraisal.entity', 1)}));
    return saveResponse;
  }

  const response = await store.$app.$http.put(`/appraisals/${id}/${action}`);
  if (response.status === 200) {
    commit('setAppraisal', response.data);
    commit('setLoading', false);
    store.$app.$snotify.success(store.$app.$t('messages.success.submitted', {entity: store.$app.$tc('models.appraisal.entity', 1)}));
  } else {
    commit('setLoading', false);
    store.$app.$snotify.error(response.errors && response.errors[0] && response.errors[0].message ?
      response.errors[0].message :
      store.$app.$t('messages.error.updated', {entity: store.$app.$tc('models.appraisal.entity', 1)}));
  }
  return response;
}

const mutations = {
  setAppraisals: (state, data) => {
    state.appraisals = new List({items: data.items.map((appraisal) => new Appraisal(appraisal)), count: data.count});
  },
  setAppraisal: (state, data) => state.appraisal = data ? new Appraisal(data) : null
};

export default {
  state,
  getters,
  actions,
  mutations
};
